import { observer } from 'mobx-react-lite'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { Select, MenuItem, FormControl, Slider, Rating, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppForm } from 'src/component/common/analog/molecule/basic/AppForm'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { Paragraph } from 'src/feature/localization/component/atom/Paragraph'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { useMyTeamStore } from 'src/feature/my-team/store/my-team.store'
import { useUserStore } from 'src/feature/user/store/user.store'
import { PerformanceReviewTemplate, QuestionType } from 'src/feature/my-team/model/my-team-performance'

export type ReviewResponseDialogProps = {
  open: boolean
  onClose: () => void
  template: PerformanceReviewTemplate
  userId?: string
  readOnly?: boolean
}

type ResponseState = {
  [questionId: string]: string
}

const prepareResponseSubmissions = (
  questions: NonNullable<PerformanceReviewTemplate['questions']>,
  responses: ResponseState,
) => {
  return questions
    .filter(question => responses[question.id])
    .map(question => ({
      questionId: question.id,
      responseValue: responses[question.id],
    }))
}

export const ReviewResponseDialog = observer(({ open, onClose, template, userId, readOnly = false }: ReviewResponseDialogProps) => {
  const { translation } = useLocalizationStore()
  const notificationStore = useNotificationStore()
  const myTeamStore = useMyTeamStore()
  const userStore = useUserStore()
  const t = (translation.pages.myTeam as any).performanceReview.responseDialog
  const [responses, setResponses] = useState<ResponseState>({})
  const { isLoading: isLoadingResponses } = myTeamStore.getGenericFieldsState('getUserResponses')

  useEffect(() => {
    if (open && template) {
      const targetUserId = userId || userStore.userData?.id

      if (targetUserId) {
        myTeamStore.getUserResponses(template.id, targetUserId)
      }

      myTeamStore.getMyAnswerComments(template.id)
    }
  }, [open, template, userId, myTeamStore, userStore.userData?.id])

  const answerCommentMap = useMemo(() => {
    const map = new Map<string, string>()
    myTeamStore.currentAnswerComments.forEach((c) => {
      map.set(c.questionId, c.content)
    })

    return map
  }, [myTeamStore.currentAnswerComments])

  useEffect(() => {
    if (open) {
      const initialResponses: ResponseState = {}
      myTeamStore.currentUserResponses.forEach((response) => {
        const questionId = typeof response.question === 'string' ? response.question : response.question.id
        initialResponses[questionId] = response.responseValue
      })
      setResponses(initialResponses)
    }
  }, [open, myTeamStore.currentUserResponses])

  const handleResponseChange = useCallback((questionId: string, value: string) => {
    setResponses(prev => ({ ...prev, [questionId]: value }))
  }, [])

  const handleClose = useCallback(() => {
    setResponses({})
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      const questions = template.questions || []
        
      // Validate required fields
      for (const question of questions) {
        if (question.isRequired && !responses[question.id]) {
          throw new Error(`${question.questionText} ${t.questionRequired()}`)
        }
      }

      const responseSubmissions = prepareResponseSubmissions(questions, responses)

      await myTeamStore.createPerformanceReviewResponses({
        templateId: template.id,
        responses: responseSubmissions,
      })

      await myTeamStore.getPerformanceReviewTemplatesByUser(userId)
      notificationStore.enqueueSuccess(t.responsesSubmittedSuccess())
      handleClose()
    } catch (error) {
      notificationStore.enqueueError(error instanceof Error ? error.message : t.responsesSubmittedFailed())
    }
  }, [myTeamStore, template, userId, responses, notificationStore, handleClose, t])

  const renderQuestionInput = useCallback((question: NonNullable<typeof template.questions>[number]) => {
    const value = responses[question.id] || ''

    switch (question.questionType) {
    case QuestionType.SLIDER:
      return (
        <AppBox flexDirection="column" gap={10}>
          <Slider
            value={Number(value) || question.metadata?.min || 0}
            onChange={(_, newValue) => handleResponseChange(question.id, String(newValue))}
            min={question.metadata?.min || 0}
            max={question.metadata?.max || 100}
            valueLabelDisplay="auto"
            marks
            disabled={readOnly}
            sx={styles.slider}
          />
          <Caption sx={styles.rangeCaption}>
            {t.range()}: {question.metadata?.min || 0} - {question.metadata?.max || 100}
          </Caption>
        </AppBox>
      )

    case QuestionType.RATING:
      return (
        <AppBox flexDirection="column" gap={10}>
          <Rating
            value={Number(value) || 0}
            onChange={(_, newValue) => handleResponseChange(question.id, String(newValue || 0))}
            max={question.metadata?.max || 5}
            size="large"
            readOnly={readOnly}
          />
          <Caption sx={styles.rangeCaption}>
            {t.rateFrom()} {question.metadata?.max || 5}
          </Caption>
        </AppBox>
      )

    case QuestionType.MULTIPLE_CHOICE:
      if (readOnly) {
        return (
          <Typography sx={styles.answerText}>{value || '-'}</Typography>
        )
      }

      return (
        <FormControl fullWidth size="small">
          <Select
            value={value}
            onChange={e => handleResponseChange(question.id, e.target.value)}
            sx={styles.select}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em>{t.selectOption()}</em>
            </MenuItem>
            {(question.metadata?.options as string[] || []).map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )

    case QuestionType.TEXTAREA:
      if (readOnly) {
        return (
          <Typography sx={styles.answerText}>{value || '-'}</Typography>
        )
      }

      return (
        <AppTextField
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleResponseChange(question.id, e.target.value)}
          fullWidth
          multiline
          rows={4}
          placeholder={t.enterResponse()}
          InputLabelProps={styles.inputLabel}
          inputProps={styles.inputProps}
        />
      )

    case QuestionType.TEXT_INPUT:
    default:
      if (readOnly) {
        return (
          <Typography sx={styles.answerText}>{value || '-'}</Typography>
        )
      }

      return (
        <AppTextField
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleResponseChange(question.id, e.target.value)}
          fullWidth
          placeholder={t.enterResponse()}
          InputLabelProps={styles.inputLabel}
          inputProps={styles.inputProps}
          sx={styles.inputField}
        />
      )
    }
  }, [responses, handleResponseChange, t, readOnly])

  return (
    <ModalWindow
      modalProps={{
        open,
        onClose: handleClose,
        sx: styles.dialogRoot,
        PaperProps: {
          sx: styles.modalPaper,
        },
      }}
      titleProps={{
        textValue: template.name,
      }}
    >
      <AppBox sx={styles.container}>
        {template.description && (
          <Paragraph sx={styles.description}>{template.description}</Paragraph>
        )}

        {isLoadingResponses ? (
          <AppSkeleton />
        ) : (
          <AppForm sx={styles.form} onSubmit={handleSubmit}>
            <AppBox flexDirection="column" gap={15} sx={styles.questionsContainer}>
              {(template.questions || []).map((question, index) => (
                <AppBox key={question.id} flexDirection="column" gap={10} sx={styles.questionBox}>
                  <AppBox alignItems="center" gap={5}>
                    <Caption sx={styles.questionNumber}>Q{index + 1}</Caption>
                    <Caption sx={styles.questionText}>
                      {question.questionText}
                      {question.isRequired && <Caption component="span" sx={styles.requiredIndicator}> *</Caption>}
                    </Caption>
                  </AppBox>
                  {answerCommentMap.get(question.id) ? (
                    <AppBox sx={styles.answerWithComment}>
                      <AppBox sx={{ flex: 1, flexDirection: 'column' }}>{renderQuestionInput(question)}</AppBox>
                      <AppBox sx={styles.managerCommentBox}>
                        <Caption sx={styles.managerCommentLabel}>
                          {(translation.pages.myTeam as any).performanceReview.meetingDetail.managerComment()}
                        </Caption>
                        <Typography sx={styles.managerCommentText}>
                          {answerCommentMap.get(question.id)}
                        </Typography>
                      </AppBox>
                    </AppBox>
                  ) : (
                    renderQuestionInput(question)
                  )}
                </AppBox>
              ))}
            </AppBox>

            <AppDivider sx={styles.divider} />

            <AppBox sx={styles.footer}>
              {readOnly ? (
                <AppButton textValue={translation.buttons.cancel} fontSize={12} variant="contained" onClick={handleClose} />
              ) : (
                <>
                  <AppButton textValue={translation.buttons.cancel} fontSize={12} variant="light" onClick={handleClose} />
                  <AppButton textValue={t.submitResponses()} fontSize={12} variant="contained" type="submit" />
                </>
              )}
            </AppBox>
          </AppForm>
        )}
      </AppBox>
    </ModalWindow>
  )
})

const styles = {
  dialogRoot: {
    zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 3,
  },
  modalPaper: {
    maxWidth: { xs: '90%', md: '60%', lg: '30%' },
    borderRadius: '10px',
  },
  container: {
    flexDirection: 'column',
    gap: 10,
  },
  description: {
    color: 'text.secondary',
    mb: 10,
  },
  form: {
    flexDirection: 'column',
    gap: 10,
    width: '100%',
  },
  questionsContainer: {
    maxHeight: '60vh',
    overflowY: 'auto',
    pr: 5,
  },
  questionBox: {
    p: 15,
    border: '0.5px solid',
    borderColor: 'secondary.main',
    borderRadius: '10px',
  },
  questionNumber: {
    fontWeight: 600,
    fontSize: 13,
    color: 'primary.main',
  },
  questionText: {
    fontWeight: 500,
    fontSize: 13,
  },
  requiredIndicator: {
    color: 'red',
  },
  slider: {
    width: '100%',
  },
  rangeCaption: {
    color: 'text.secondary',
    fontSize: 11,
  },
  inputLabel: {
    sx: { fontSize: 12 },
  },
  inputProps: {
    sx: { fontSize: 12 },
  },
  inputField: {
    '& .MuiInputBase-root': { height: '40px' },
  },
  select: {
    fontSize: 12,
    height: '40px',
    backgroundColor: 'background.default',
    '& .MuiSelect-select': {
      padding: '10px 12px',
      fontSize: 12,
    },
  },
  divider: {
    mt: 10,
    mb: 10,
  },
  footer: {
    gap: 10,
    justifyContent: 'flex-end',
    width: '100%',
  },
  answerText: {
    fontSize: 12,
    color: 'text.secondary',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  answerWithComment: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
  },
  managerCommentBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    p: 8,
    backgroundColor: 'action.hover',
    borderRadius: '10px',
  },
  managerCommentLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'text.secondary',
  },
  managerCommentText: {
    fontSize: 12,
    color: 'text.primary',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}
